import { Injectable, inject } from '@angular/core'
import { IlsModelService } from './ils-model.service'
import { ActivityStreamObject, Generator, logActionVerbs, Provider, Target } from '@monorepo/next-lab'
import { AddLogActionDto, LogActionService } from '@monorepo/golab-generated'
import { TokenService } from '@monorepo/auth'

const debug = false

@Injectable({
  providedIn: 'root',
})
export class SendLogActionService {
  private readonly ilsModelService = inject(IlsModelService);
  private readonly logActionService = inject(LogActionService);
  private readonly tokenService = inject(TokenService);

  private playerSessionSequenceNumber = 0
  private ilsAppSessionSequenceNumber = 0

  initialize() {
    // nothing to initialize
  }

  clear() {
    this.resetSequenceNumbers()
  }

  sendLogAction(logAction: ActivityStreamObject) {
    if (!this.ilsModelService.learningAnalyticsEnabled$.value || this.ilsModelService.published) {
      return
    }
    // @ts-ignore // the compiler thinks ActivityStreamObject.object.id is of type string | undefined, but it is just an optional string
    const addLogActionDto: AddLogActionDto = {
      ...logAction,
      sessionSequenceNumber: this.playerSessionSequenceNumber++,
    }
    if (!addLogActionDto.published) {
      addLogActionDto.published = new Date().toISOString()
    }
    // console.log(`IP: sending log action`, addLogActionDto)
    this.logActionService
      .logActionControllerAdd({
        body: addLogActionDto,
      })
      .subscribe({
        // @ts-ignore
        next: (date: Date) => {
          if (debug) {
            console.log(`IP: log action added`, addLogActionDto, date)
          }
        },
        error: (error: any) => {
          console.error(`IP: error adding log action`, error)
        },
      })
  }

  sendLoginAction() {
    this.resetSequenceNumbers()
    this.sendLogAction(this.createLogAction(logActionVerbs.start, this.getIlsTarget(), this.getIlsGenerator(), true))
  }

  sendLogoutAction() {
    this.sendLogAction(this.createLogAction(logActionVerbs.stop, this.getIlsTarget(), this.getIlsGenerator(), true))
    this.resetSequenceNumbers()
  }

  sendEnterIlsAction() {
    this.sendLogAction(this.createLogAction(logActionVerbs.access, this.getIlsTarget(), this.getIlsGenerator(), true))
  }

  sendLeaveIlsAction() {
    this.sendLogAction(this.createLogAction(logActionVerbs.cancel, this.getIlsTarget(), this.getIlsTarget(), true))
  }

  sendEnterPhaseAction() {
    this.sendLogAction(
      this.createLogAction(logActionVerbs.access, this.getCurrentPhaseTarget(), this.getCurrentPhaseTarget(), false),
    )
  }

  sendLeavePhaseAction() {
    this.sendLogAction(
      this.createLogAction(logActionVerbs.cancel, this.getCurrentPhaseTarget(), this.getCurrentPhaseTarget(), false),
    )
  }

  sendToggleIlsNavigationDrawerAction(newVisible: boolean) {
    this.sendToggleDrawerLogAction('ilsNavigationDrawer', newVisible)
  }

  sendToggleIlsGlobalAppsDrawerAction(newVisible: boolean) {
    this.sendToggleDrawerLogAction('ilsGlobalAppsDrawer', newVisible)
  }

  private resetSequenceNumbers() {
    this.playerSessionSequenceNumber = 0
    this.ilsAppSessionSequenceNumber = 0
  }

  private getIlsGenerator(): Generator {
    return {
      id: this.ilsModelService.ilsId,
      objectType: 'ils',
      displayName: this.ilsModelService.title$.value,
      url: this.ilsModelService.url,
    }
  }

  private getIlsTarget(): Target {
    return {
      id: this.ilsModelService.ilsId,
      objectType: 'ils',
      displayName: this.ilsModelService.title$.value,
    }
  }

  private getCurrentPhaseTarget(): Target {
    const currentPhase = this.ilsModelService.currentPhase
    return {
      id: currentPhase._id,
      objectType: 'phase',
      displayName: currentPhase.title,
    }
  }

  private getProvider(ilsLevel: boolean = false): Provider {
    const provider = {
      id: this.ilsModelService.ilsId,
      objectType: 'ils',
      displayName: this.ilsModelService.title$.value,
      url: this.ilsModelService.url,
      inquiryPhaseId: 'undefined',
      inquiryPhaseName: 'undefined',
      inquiryPhase: 'undefined',
      ilsHasAngeLA: this.ilsModelService.learningAnalyticsEnabled$.value,
    }
    if (this.ilsModelService.currentPhaseId && !ilsLevel) {
      const currentPhase = this.ilsModelService.currentPhase
      provider.inquiryPhaseId = currentPhase._id
      provider.inquiryPhaseName = currentPhase.title
    }
    return provider
  }

  private createLogAction(verb: logActionVerbs, target: Target, object: any, ilsLevel: boolean): ActivityStreamObject {
    return {
      actor: this.tokenService.getActor(),
      generator: this.getIlsGenerator(),
      provider: this.getProvider(ilsLevel),
      target: target,
      verb: verb,
      object: object,
      published: new Date().toISOString(),
      appSequenceNumber: this.ilsAppSessionSequenceNumber++,
    }
  }

  private sendToggleDrawerLogAction(label: string, newVisible: boolean) {
    const toggleDrawerState = {
      objectType: label,
      content: {
        visible: newVisible,
      },
    }
    this.sendLogAction(this.createLogAction(logActionVerbs.change, this.getIlsTarget(), toggleDrawerState, true))
  }
}
